import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, ShoppingBag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import StoneRequestDialog from '../components/StoneRequestDialog';

const birthstones = [
  {
    month: 'January',
    stone: 'Garnet',
    color: 'Deep Red',
    image: 'https://customer-assets.emergentagent.com/job_luxury-gems-27/artifacts/57ay2kuc_Untitled%20design%20-%201.png',
    benefits: {
      success: 'Enhances career advancement and brings prosperity in business ventures. Strengthens leadership qualities and promotes professional recognition.',
      luck: 'Attracts positive energy and wards off negative influences. Creates opportunities for advancement and unexpected good fortune.',
      love: 'Deepens romantic connections and strengthens commitment. Reignites passion in long-term relationships and attracts loyal partners.',
      health: 'Boosts energy levels and vitality. Supports blood circulation and strengthens the immune system. Promotes physical endurance.',
      wealth: 'Opens pathways to financial abundance and stability. Helps manifest prosperity and wise investment decisions.'
    },
    description: 'A stone of passion and vitality, Garnet is believed to energize the body and spirit. Known as the "stone of commitment," it has been cherished for centuries for its ability to inspire devotion and bring success in all endeavors.'
  },
  {
    month: 'February',
    stone: 'Amethyst',
    color: 'Purple',
    image: 'https://customer-assets.emergentagent.com/job_luxury-gems-27/artifacts/bdteumt1_Untitled%20design%20-%202.png',
    benefits: {
      success: 'Promotes clarity of mind and wise decision-making in professional matters. Enhances intuition and strategic thinking for career growth.',
      luck: 'Opens doors to new opportunities and spiritual growth. Attracts beneficial connections and synchronistic events.',
      love: 'Encourages inner peace and harmony in relationships. Promotes emotional balance and understanding between partners.',
      health: 'Calms the mind and relieves stress. Supports peaceful sleep and mental clarity. Alleviates anxiety and promotes emotional healing.',
      wealth: 'Brings financial wisdom and guards against impulsive spending. Attracts prosperity through mindful decision-making.'
    },
    description: 'Known as the stone of spiritual wisdom, Amethyst brings balance and tranquility. This "stone of sobriety" has been revered since ancient times for its calming energy and ability to enhance spiritual awareness and intellectual power.'
  },
  {
    month: 'March',
    stone: 'Aquamarine',
    color: 'Pale Blue',
    image: 'https://customer-assets.emergentagent.com/job_luxury-gems-27/artifacts/jrw699b0_Untitled%20design%20-%203.png',
    benefits: {
      success: 'Enhances communication skills and leadership qualities. Inspires confidence in public speaking and professional presentations.',
      luck: 'Brings courage to overcome challenges and seize opportunities. Clears obstacles on the path to achievement.',
      love: 'Fosters trust and emotional clarity in partnerships. Promotes open communication and understanding in relationships.',
      health: 'Soothes throat and respiratory ailments. Calms the nervous system and reduces stress. Promotes overall physical wellness.',
      wealth: 'Attracts abundance through clear communication and networking. Opens doors to lucrative partnerships and collaborations.'
    },
    description: 'The serene stone of the sea, Aquamarine calms the mind and inspires truth. Known as the "sailor\'s stone," it has protected travelers and brought courage to warriors, while promoting honest communication and peaceful resolution.'
  },
  {
    month: 'April',
    stone: 'Diamond',
    color: 'Clear/White',
    image: 'https://customer-assets.emergentagent.com/job_luxury-gems-27/artifacts/l490yj5t_Untitled%20design%20-%204.png',
    benefits: {
      success: 'Amplifies personal power and manifests abundance. Strengthens determination and brings clarity to goals and ambitions.',
      luck: 'Attracts prosperity and protects against negative energy. Creates a shield of positive vibrations around the wearer.',
      love: 'Symbolizes eternal love and strengthens bonds. Enhances fidelity, commitment, and deep emotional connections.',
      health: 'Amplifies overall vitality and life force energy. Strengthens the physical body and promotes longevity.',
      wealth: 'The ultimate stone of abundance and material success. Attracts wealth, luxury, and financial opportunities.'
    },
    description: 'The ultimate symbol of strength and purity, Diamond radiates invincible energy. As the "king of gemstones," it represents eternal love, unbreakable bonds, and the manifestation of one\'s highest potential.'
  },
  {
    month: 'May',
    stone: 'Emerald',
    color: 'Rich Green',
    image: 'https://customer-assets.emergentagent.com/job_luxury-gems-27/artifacts/oq8flnpg_Untitled%20design%20-%205.png',
    benefits: {
      success: 'Opens pathways to wealth and promotes successful ventures. Enhances business acumen and attracts career opportunities.',
      luck: 'Brings good fortune in financial matters and investments. Known as the "stone of successful love" attracting prosperity.',
      love: 'Nurtures unconditional love and deepens emotional connections. Promotes loyalty, partnership, and domestic bliss.',
      health: 'Strengthens the heart and supports emotional healing. Promotes vitality, recovery, and overall physical wellness.',
      wealth: 'The ultimate stone of abundance and financial growth. Attracts wealth, prosperity, and material success in all forms.'
    },
    description: 'The stone of abundance and compassion, Emerald attracts prosperity and love. Revered by Cleopatra and ancient civilizations, this "stone of successful love" has been treasured for millennia as a symbol of hope, renewal, and infinite patience.'
  },
  {
    month: 'June',
    stone: 'Pearl & Alexandrite',
    color: 'White/Color-changing',
    image: 'https://customer-assets.emergentagent.com/job_luxury-gems-27/artifacts/6m4jhhes_Untitled%20design%20-%206.png',
    benefits: {
      success: 'Enhances wisdom and brings balance to professional life. Promotes adaptability and grace under pressure.',
      luck: 'Brings transformation and adaptability to changing circumstances. Attracts fortunate opportunities and positive outcomes.',
      love: 'Promotes purity of heart and genuine affection. Strengthens emotional bonds and nurtures loving relationships.',
      health: 'Calms emotions and promotes inner peace. Balances body systems and enhances natural healing abilities.',
      wealth: 'Attracts prosperity through wisdom and patience. Brings financial stability and long-term wealth accumulation.'
    },
    description: 'Pearls offer wisdom and serenity, while Alexandrite brings joy and good fortune. Pearl, the "queen of gems," symbolizes purity and feminine energy, while Alexandrite, the "emerald by day, ruby by night," represents transformation and balance.'
  },
  {
    month: 'July',
    stone: 'Ruby',
    color: 'Brilliant Red',
    image: 'https://customer-assets.emergentagent.com/job_luxury-gems-27/artifacts/03zaz4z7_Untitled%20design%20-%207.png',
    benefits: {
      success: 'Ignites passion and motivation for achieving goals. Enhances confidence, courage, and leadership abilities.',
      luck: 'Attracts wealth, power, and protection from harm. Creates a powerful shield against negative energies.',
      love: 'Intensifies romantic passion and devotion. Reignites the flame of love and strengthens intimate connections.',
      health: 'Energizes the body and promotes vitality. Supports circulatory system and enhances physical stamina.',
      wealth: 'Attracts prosperity, abundance, and material success. Known as the "king of precious stones" for attracting wealth.'
    },
    description: 'The king of gemstones, Ruby embodies courage, vitality, and passionate love. Revered as the "stone of nobility," it has adorned crowns and been prized by royalty for its ability to bring success, power, and protection to its wearer.'
  },
  {
    month: 'August',
    stone: 'Peridot',
    color: 'Olive Green',
    image: 'https://customer-assets.emergentagent.com/job_luxury-gems-27/artifacts/z1p5s5e6_Untitled%20design%20-%208.png',
    benefits: {
      success: 'Brings abundance and removes obstacles to prosperity. Enhances confidence and attracts recognition for achievements.',
      luck: 'Protects against negativity and attracts good fortune. Creates positive energy and fortunate circumstances.',
      love: 'Heals relationships and promotes forgiveness. Strengthens bonds and brings harmony to partnerships.',
      health: 'Cleanses and purifies the body. Supports digestion and promotes overall physical healing and wellness.',
      wealth: 'Attracts financial opportunities and material abundance. Helps manifest prosperity and overcome financial obstacles.'
    },
    description: 'The stone of compassion, Peridot brings healing light and positive energy. Known as the "gem of the sun," this ancient Egyptian stone was called "the gem of the sun" and believed to protect against nightmares while attracting wealth and happiness.'
  },
  {
    month: 'September',
    stone: 'Sapphire',
    color: 'Deep Blue',
    image: 'https://customer-assets.emergentagent.com/job_luxury-gems-27/artifacts/il3pywae_Untitled%20design%20-%209.png',
    benefits: {
      success: 'Enhances focus, discipline, and achievement of long-term goals. Promotes wisdom, integrity, and professional excellence.',
      luck: 'Brings wisdom and protection in important decisions. Attracts blessings and divine favor.',
      love: 'Fosters loyalty, trust, and lasting commitment. Strengthens relationships built on honesty and mutual respect.',
      health: 'Calms the mind and reduces stress. Supports mental clarity, focus, and overall neurological health.',
      wealth: 'Attracts wealth through wisdom and good judgment. Brings financial stability and long-term prosperity.'
    },
    description: 'The stone of wisdom and royalty, Sapphire brings mental clarity and spiritual insight. Known as the "stone of destiny," it has been cherished by kings and priests for centuries, believed to bring divine favor and protection.'
  },
  {
    month: 'October',
    stone: 'Opal & Tourmaline',
    color: 'Multi-color/Pink',
    image: 'https://customer-assets.emergentagent.com/job_luxury-gems-27/artifacts/ncnay6rp_Untitled%20design%20-%2010.png',
    benefits: {
      success: 'Stimulates creativity and inspires innovation. Enhances artistic abilities and original thinking in professional endeavors.',
      luck: 'Attracts good luck and amplifies positive emotions. Brings joy, spontaneity, and serendipitous events.',
      love: 'Enhances passion and spontaneity in romance. Awakens desire and deepens emotional connections.',
      health: 'Strengthens will to live and overcome illness. Promotes emotional healing and releases old traumas.',
      wealth: 'Attracts abundance through creative expression. Opens doors to profitable creative ventures and opportunities.'
    },
    description: 'Opal awakens creativity, while Tourmaline brings inspiration and compassion. Opal, the "stone of hope," displays all colors of the rainbow, while Tourmaline\'s varied colors are believed to attract different forms of abundance and joy.'
  },
  {
    month: 'November',
    stone: 'Topaz & Citrine',
    color: 'Golden Yellow',
    image: 'https://customer-assets.emergentagent.com/job_luxury-gems-27/artifacts/w124iaw4_Untitled%20design%20-%2011.png',
    benefits: {
      success: 'Attracts abundance and manifests prosperity. Enhances confidence, personal power, and achievement of goals.',
      luck: 'Brings joy, optimism, and successful outcomes. Known as the "merchant\'s stone" for attracting business success.',
      love: 'Encourages warmth, generosity, and positive relationships. Promotes happiness and emotional openness.',
      health: 'Energizes and revitalizes the body. Supports metabolism, digestion, and overall physical vitality.',
      wealth: 'The ultimate stone of prosperity and financial success. Attracts wealth, abundance, and material blessings.'
    },
    description: 'Stones of abundance and joy, they bring warmth, prosperity, and happiness. Citrine, the "success stone," has been called the "merchant\'s stone" for its ability to attract wealth, while Topaz brings strength and wisdom.'
  },
  {
    month: 'December',
    stone: 'Turquoise, Tanzanite & Zircon',
    color: 'Sky Blue/Purple-Blue',
    image: 'https://customer-assets.emergentagent.com/job_luxury-gems-27/artifacts/5pcgi045_Untitled%20design%20-%2012.png',
    benefits: {
      success: 'Enhances communication and brings good fortune in endeavors. Promotes self-expression and authentic leadership.',
      luck: 'Provides protection and attracts prosperity. Creates a shield of positive energy and good fortune.',
      love: 'Promotes peace, understanding, and emotional balance. Strengthens friendship and family bonds.',
      health: 'Protects overall health and promotes healing. Strengthens the immune system and supports respiratory health.',
      wealth: 'Attracts financial opportunities and material success. Brings prosperity through communication and networking.'
    },
    description: 'These stones bring protection, prosperity, and spiritual elevation. Turquoise, the "master healer," has been treasured for thousands of years as a protective talisman, while Tanzanite and Zircon bring clarity and transformation.'
  }
];

const BirthstonesPage = () => {
  const [selectedMonth, setSelectedMonth] = useState('January'); // Default to January
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const selectedStone = selectedMonth ? birthstones.find(b => b.month === selectedMonth) : null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-black to-gray-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Sparkles className="w-16 h-16 mx-auto mb-6 text-yellow-400" />
            <h1 className="font-serif text-5xl md:text-6xl mb-4">Birthstones</h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Discover the mystical properties of your birthstone and how it can bring success, luck, and love into your life
            </p>
          </motion.div>
        </div>
      </div>

      {/* Month Selector */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <h2 className="text-3xl font-serif text-center mb-8">Select Your Birth Month</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {birthstones.map((item) => (
            <motion.button
              key={item.month}
              onClick={() => setSelectedMonth(item.month)}
              className={`p-4 rounded-lg border-2 transition-all relative overflow-hidden ${
                selectedMonth === item.month
                  ? 'border-black bg-black text-white shadow-lg scale-105'
                  : 'border-gray-200 hover:border-gray-400 hover:shadow-md'
              }`}
              whileHover={{ scale: selectedMonth === item.month ? 1.05 : 1.03 }}
              whileTap={{ scale: 0.98 }}
            >
              {selectedMonth === item.month && (
                <motion.div
                  layoutId="selectedBg"
                  className="absolute inset-0 bg-gradient-to-br from-black to-gray-800"
                  initial={false}
                  transition={{ type: "spring", stiffness: 500, damping: 30 }}
                />
              )}
              <div className="relative z-10">
                <div className="font-medium mb-1">{item.month}</div>
                <div className="text-sm opacity-75">{item.stone}</div>
                {selectedMonth === item.month && (
                  <div className="mt-2 text-xs text-yellow-400">✨ Selected</div>
                )}
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Stone Details */}
      <AnimatePresence mode="wait">
        {selectedStone && (
          <motion.div
            key={selectedStone.month}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5 }}
            className="max-w-6xl mx-auto px-4 pb-20"
          >
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
              <div className="grid md:grid-cols-2 gap-8">
                {/* Image */}
                <div className="relative h-96 md:h-full">
                  <img
                    src={selectedStone.image}
                    alt={selectedStone.stone}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  <div className="absolute bottom-6 left-6 text-white">
                    <h3 className="font-serif text-4xl mb-2">{selectedStone.stone}</h3>
                    <p className="text-lg">{selectedStone.color}</p>
                  </div>
                </div>

                {/* Details */}
                <div className="p-8">
                  <p className="text-lg text-gray-700 mb-6 italic leading-relaxed">"{selectedStone.description}"</p>

                  <div className="space-y-4 mb-8">
                    <div className="border-l-4 border-green-500 pl-4 bg-green-50 py-3 rounded-r">
                      <h4 className="font-semibold text-green-700 mb-2 flex items-center gap-2">
                        <span className="text-xl">✨</span> Success & Career
                      </h4>
                      <p className="text-gray-700 text-sm leading-relaxed">{selectedStone.benefits.success}</p>
                    </div>
                    <div className="border-l-4 border-yellow-500 pl-4 bg-yellow-50 py-3 rounded-r">
                      <h4 className="font-semibold text-yellow-700 mb-2 flex items-center gap-2">
                        <span className="text-xl">🍀</span> Luck & Fortune
                      </h4>
                      <p className="text-gray-700 text-sm leading-relaxed">{selectedStone.benefits.luck}</p>
                    </div>
                    <div className="border-l-4 border-red-500 pl-4 bg-red-50 py-3 rounded-r">
                      <h4 className="font-semibold text-red-700 mb-2 flex items-center gap-2">
                        <span className="text-xl">❤️</span> Love & Relationships
                      </h4>
                      <p className="text-gray-700 text-sm leading-relaxed">{selectedStone.benefits.love}</p>
                    </div>
                    <div className="border-l-4 border-blue-500 pl-4 bg-blue-50 py-3 rounded-r">
                      <h4 className="font-semibold text-blue-700 mb-2 flex items-center gap-2">
                        <span className="text-xl">💪</span> Health & Vitality
                      </h4>
                      <p className="text-gray-700 text-sm leading-relaxed">{selectedStone.benefits.health}</p>
                    </div>
                    <div className="border-l-4 border-purple-500 pl-4 bg-purple-50 py-3 rounded-r">
                      <h4 className="font-semibold text-purple-700 mb-2 flex items-center gap-2">
                        <span className="text-xl">💰</span> Wealth & Prosperity
                      </h4>
                      <p className="text-gray-700 text-sm leading-relaxed">{selectedStone.benefits.wealth}</p>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-3">
                    <Button
                      onClick={() => navigate('/shop')}
                      className="w-full bg-black hover:bg-gray-800 text-white"
                      size="lg"
                    >
                      <ShoppingBag className="w-4 h-4 mr-2" />
                      Shop All Gemstones
                    </Button>
                    
                    {isAuthenticated() && (
                      <Button
                        onClick={() => setShowRequestDialog(true)}
                        variant="outline"
                        className="w-full"
                        size="lg"
                      >
                        Request This Stone
                      </Button>
                    )}
                    
                    {!isAuthenticated() && (
                      <p className="text-sm text-gray-500 text-center">
                        <button
                          onClick={() => navigate('/login')}
                          className="text-black underline hover:text-gray-700"
                        >
                          Sign in
                        </button>
                        {' '}to request a specific stone
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Call to Action */}
      {!selectedStone && (
        <div className="max-w-4xl mx-auto px-4 pb-20 text-center">
          <p className="text-gray-600 text-lg">
            Select your birth month above to discover your birthstone's mystical properties and how it can enhance your life journey.
          </p>
        </div>
      )}

      {/* Stone Request Dialog */}
      {showRequestDialog && selectedStone && (
        <StoneRequestDialog
          isOpen={showRequestDialog}
          onClose={() => setShowRequestDialog(false)}
          defaultStone={selectedStone.stone}
        />
      )}
    </div>
  );
};

export default BirthstonesPage;
